import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // "email" | "code" | "done"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/client/forgot-password", { email });
      toast.success("Reset code sent — check your email");
      setStep("code");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset code");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/client/reset-password", { email, code, new_password: newPassword });
      toast.success("Password updated!");
      setStep("done");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      {/* Left brand panel */}
      <div
        style={{
          flex: "0 0 55%",
          backgroundColor: "#C41E1E",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "3rem",
          position: "relative",
        }}
        className="hidden md:flex"
      >
        <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "3rem", color: "#FFFFFF", letterSpacing: "6px", marginBottom: "1rem" }}>
          ELEMENTAR
        </h1>
        <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(255,255,255,0.4)", marginBottom: "1.5rem" }} />
        <p style={{ fontFamily: "'Cormorant Garamond', Garamond, serif", fontWeight: 400, fontSize: "1.15rem", color: "rgba(255,255,255,0.85)", letterSpacing: "3px", textTransform: "uppercase", textAlign: "center" }}>
          Unified alternative investment intelligence
        </p>
        <p style={{ position: "absolute", bottom: "2rem", fontFamily: "'DM Sans', sans-serif", fontSize: "0.7rem", letterSpacing: "3px", color: "rgba(255,255,255,0.4)", textTransform: "uppercase" }}>
          From Wooden Owl AG
        </p>
      </div>

      {/* Right form panel */}
      <div style={{ flex: 1, backgroundColor: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
        <div style={{ width: "100%", maxWidth: "380px" }}>
          {/* Mobile logo */}
          <div className="md:hidden" style={{ textAlign: "center", marginBottom: "2rem" }}>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "1.8rem", color: "#C41E1E", letterSpacing: "4px" }}>
              ELEMENTAR
            </h2>
          </div>

          {step === "done" ? (
            <div style={{ textAlign: "center" }}>
              <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400, fontSize: "1.75rem", color: "#F0EDE8", marginBottom: "1rem" }}>
                Password updated
              </h2>
              <p style={{ color: "#8A8A8A", fontSize: "0.9rem" }}>
                Redirecting to login...
              </p>
            </div>
          ) : (
            <>
              <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400, fontSize: "1.75rem", color: "#F0EDE8", marginBottom: "0.5rem" }}>
                {step === "email" ? "Reset your password" : "Enter reset code"}
              </h2>
              <p style={{ fontFamily: "'Cormorant Garamond', Garamond, serif", fontSize: "1rem", color: "#8A8A8A", marginBottom: "2.5rem" }}>
                {step === "email"
                  ? "We'll send a 6-digit code to your email"
                  : `Enter the code sent to ${email}`}
              </p>

              <form onSubmit={step === "email" ? handleSendCode : handleReset} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                {step === "email" && (
                  <div>
                    <label htmlFor="email" style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: "0.75rem", fontWeight: 500, color: "#8A8A8A", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@office.com"
                      required
                      style={{ width: "100%", padding: "0.85rem 1rem", backgroundColor: "#141414", border: "1px solid #222", borderRadius: "6px", color: "#F0EDE8", fontFamily: "'Cormorant Garamond', Garamond, serif", fontSize: "1rem", outline: "none" }}
                      onFocus={(e) => (e.target.style.borderColor = "#C41E1E")}
                      onBlur={(e) => (e.target.style.borderColor = "#222")}
                    />
                  </div>
                )}

                {step === "code" && (
                  <>
                    <div>
                      <label htmlFor="code" style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: "0.75rem", fontWeight: 500, color: "#8A8A8A", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                        Reset Code
                      </label>
                      <input
                        id="code"
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="6-digit code"
                        required
                        maxLength={6}
                        style={{ width: "100%", padding: "0.85rem 1rem", backgroundColor: "#141414", border: "1px solid #222", borderRadius: "6px", color: "#F0EDE8", fontFamily: "'Cormorant Garamond', Garamond, serif", fontSize: "1.5rem", letterSpacing: "8px", textAlign: "center", outline: "none" }}
                        onFocus={(e) => (e.target.style.borderColor = "#C41E1E")}
                        onBlur={(e) => (e.target.style.borderColor = "#222")}
                      />
                    </div>
                    <div>
                      <label htmlFor="new-password" style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: "0.75rem", fontWeight: 500, color: "#8A8A8A", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "0.5rem" }}>
                        New Password
                      </label>
                      <input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Minimum 8 characters"
                        required
                        minLength={8}
                        style={{ width: "100%", padding: "0.85rem 1rem", backgroundColor: "#141414", border: "1px solid #222", borderRadius: "6px", color: "#F0EDE8", fontFamily: "'Cormorant Garamond', Garamond, serif", fontSize: "1rem", outline: "none" }}
                        onFocus={(e) => (e.target.style.borderColor = "#C41E1E")}
                        onBlur={(e) => (e.target.style.borderColor = "#222")}
                      />
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    width: "100%", padding: "0.85rem", backgroundColor: "#C41E1E", color: "#FFFFFF",
                    border: "none", borderRadius: "6px", fontFamily: "'DM Sans', sans-serif",
                    fontSize: "0.8rem", fontWeight: 500, letterSpacing: "2px", textTransform: "uppercase",
                    cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1,
                    transition: "background-color 0.3s",
                  }}
                  onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = "#8B1515"; }}
                  onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = "#C41E1E"; }}
                >
                  {submitting ? "Sending..." : step === "email" ? "Send Reset Code" : "Reset Password"}
                </button>
              </form>

              <p style={{ marginTop: "2rem", textAlign: "center" }}>
                <button
                  onClick={() => navigate("/login")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#8A8A8A", fontSize: "0.85rem", fontFamily: "'DM Sans', sans-serif" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#F0EDE8")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#8A8A8A")}
                >
                  Back to Sign In
                </button>
              </p>
            </>
          )}

          <div style={{ marginTop: "3rem", textAlign: "center" }}>
            <p style={{ fontSize: "0.65rem", color: "#444" }}>© 2026 Elementar</p>
          </div>
        </div>
      </div>
    </div>
  );
}
